import cn from "classnames";
import classNames from "classnames";
import If from "../../components/utils/If";
import IconSortLetterDesc from "../../components/icon/IconSortLetterDesc";
import IconSortLetterAsc from "../../components/icon/IconSortLetterAsc";
import React, {PropsWithChildren, useEffect, useState} from "react";
import {
  SortingRule,
  TableOptions,
  TableState,
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from "react-table";
import {useLocalStorage} from "../../utils/useLocalStorage";
import {useDebounce} from "../../utils/useDebounce";
import {TablePagination} from "./TablePagination";
import IconSortNumberDesc from "../../components/icon/IconSortNumberDesc";
import IconSortNumberAsc from "../../components/icon/IconSortNumberAsc";
import TableSearch from "./TableSearch";
import TableFilter, {FilterBlock, FilterState} from "./TableFilter";

export interface TableFilter {
  search?: string
  filters: FilterState[]
}

export interface TableProperties<T extends Record<string, unknown>, K extends TableFilter> extends TableOptions<T> {
  name: string
  reset: boolean
  total: number
  onFetchData?: (page: number, pageSize: number, sortBy: SortingRule<T>[], filter: K) => void
  onClick?: (row: T) => void
  error?: string
  loading: boolean
  filters: FilterBlock[]
  initialFilterState?: FilterState[]
  buttons?: React.ReactNode
  noSearch?: boolean;
  noPagination?: boolean;
}

export default function Table<T extends Record<string, unknown>, K extends TableFilter>(props: PropsWithChildren<TableProperties<T, K>>) {
  const {
    name,
    columns,
    data,
    reset,
    total,
    onFetchData,
    onClick,
    loading,
    error,
    filters,
    buttons,
    initialFilterState,
    noPagination = false,
    noSearch = false
  } = props

  const DEFAULT_PAGE_SIZE = 25;
  const emptyStates = filters.map(x => new FilterState(x.id, false, ''));
  const [initialState, setInitialState] = useLocalStorage<Partial<TableState<T>>>(`tableState:${name}`, {
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: [],
    globalFilter: {filters: initialFilterState || emptyStates}
  });

  const manualFetch = !!onFetchData;

  const instance = useTable<T>({
    columns,
    data,
    initialState,
    pageCount: Math.ceil(total / DEFAULT_PAGE_SIZE),
    manualPagination: manualFetch,
    manualSortBy: manualFetch,
    manualGlobalFilter: manualFetch,
    autoResetPage: !reset,
    autoResetExpanded: !reset,
    autoResetGroupBy: !reset,
    autoResetSelectedRows: !reset,
    autoResetSortBy: !reset,
    autoResetGlobalFilter: !reset,
  }, useGlobalFilter, useSortBy, usePagination, useRowSelect);

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    state
  } = instance

  const debouncedState = useDebounce(state, 50)
  const emptyCall = () => undefined;
  const onFetchDataDebounced = useAsyncDebounce(onFetchData || emptyCall, 200);

  const onReset = () => {
    setInitialState({...initialState, sortBy: [], pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE, globalFilter: undefined})
    const filter: any = undefined
    if (onFetchData) {
      onFetchDataDebounced(0, DEFAULT_PAGE_SIZE, [], filter)
    }
  }
  const [loadingWidth, setLoadingWidth] = useState(0)

  useEffect(() => {
    if (loading && loadingWidth != 100) {
      if (loadingWidth > 95) {
        setLoadingWidth(0);
      }
      const percentage = setTimeout(() => {
        setLoadingWidth(loadingWidth + 1)
      }, 30);
      return () => {
        clearTimeout(percentage);
      };
    } else {
      return () => {
        setLoadingWidth(0);
      };
    }
  }, [loadingWidth, loading])

  useEffect(() => {
    const {sortBy, globalFilter, pageSize, pageIndex} = debouncedState
    setInitialState({...initialState, sortBy, pageSize, pageIndex, globalFilter})
    if (onFetchData) {
      onFetchDataDebounced(pageIndex, pageSize, sortBy, globalFilter ? globalFilter : {filters})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFetchDataDebounced, debouncedState])

  const onApplyFilters = (appliedFilters: FilterState[]) => {
    instance.setGlobalFilter({...state.globalFilter, filters: appliedFilters});
    instance.gotoPage(0);
  }
  const onApplySearch = (value: string) => {
    instance.setGlobalFilter({...state.globalFilter, search: value});
    instance.gotoPage(0);
  }

  return (
    <>
      {!noSearch &&
        <div className="flex flex-row justify-between mb-5 rounded-xl bg-gray-light p-4">
          <div className="flex items-center">
            <TableSearch onSearch={onApplySearch}
                         value={state.globalFilter?.search || ""}/>
            {filters.length > 0 &&
              <TableFilter states={state.globalFilter?.filters || emptyStates} filters={filters}
                           onApply={onApplyFilters}/>
            }
          </div>
          <div className="flex items-center">
            {buttons}
          </div>
        </div>
      }
      <table className="min-w-full divide-y divide-gray" {...getTableProps()}>
        <thead>
        {headerGroups && headerGroups.map((headerGroup, idx) => (
          <tr {...headerGroup.getHeaderGroupProps()} key={idx}>
            {headerGroup.headers.map(column => (
              <th {...column.getHeaderProps(column.getSortByToggleProps())} key={column.id}
                  className={cn("px-3 py-3 text-left font-bold", {'cursor-pointer': column.canSort})}
              >
                <div className="flex flex-row items-center">
                  {column.render('Header')}
                  <If condition={column.canSort}>
                    <If condition={column.sortType === "number"}>
                      {column.isSortedDesc ? <IconSortNumberDesc className={cn('cursor-pointer ml-1 mt-0.5 h-4', {
                          'text-gray': !column.isSorted,
                          'text-blue': column.isSorted
                        })}/> :
                        <IconSortNumberAsc className={cn('cursor-pointer ml-1 mt-0.5 h-4', {
                          'text-gray': !column.isSorted,
                          'text-blue': column.isSorted
                        })}/>}
                    </If>
                    <If condition={column.sortType !== "number"}>
                      {column.isSortedDesc ? <IconSortLetterDesc className={cn('cursor-pointer ml-1 mt-0.5 h-4', {
                          'text-gray': !column.isSorted,
                          'text-blue': column.isSorted
                        })}/> :
                        <IconSortLetterAsc className={cn('cursor-pointer ml-1 mt-0.5 h-4', {
                          'text-gray': !column.isSorted,
                          'text-blue': column.isSorted
                        })}/>}
                    </If>
                  </If>
                </div>
              </th>
            ))}
          </tr>
        ))}
        </thead>
        <tbody className={"bg-white divide-y divide-gray-light relative z-0"} {...getTableBodyProps()}>
        {rows &&
          <>
            {rows.length > 0 && rows.map((row, idx) => {
              prepareRow(row)
              return (
                <tr
                  className={classNames("hover:bg-blue-table-hover", {"cursor-pointer": onClick})}
                  {...row.getRowProps()}
                  key={row.id}
                  onClick={() => onClick && onClick(row.original)}>
                  {row.cells.map((cell, id) => {
                    const {key: cellKey, role: cellRole, ...getCellProps} = cell.getCellProps()
                    return <td {...cell.getCellProps()} key={cellKey}
                               className={cn("px-3 py-4 whitespace-nowrap text-sm", {"relative": id === 0})}>
                      {id === 0 && loading &&
                        <div className="absolute bg-gray left-0 top-0 bottom-0 opacity-40"
                             style={{width: `${loadingWidth}%`}}></div>
                      }
                      {cell.render('Cell')}
                    </td>
                  })}
                </tr>
              );
            })}
            {rows.length === 0 && loading && <tr>
              <td colSpan={columns.length} className="p-6 text-xl text-center">
                <div className="absolute bg-gray left-0 top-0 bottom-0 opacity-40"
                     style={{width: `${loadingWidth/5}%`}}></div>
                Loading...
              </td>
            </tr>}
            {rows.length === 0 && !loading && <tr>
              <td colSpan={columns.length} className="p-6 text-xl text-center">No records found</td>
            </tr>}
          </>
        }
        </tbody>
      </table>
      {!noPagination &&
        <TablePagination<T> instance={instance} total={total} loading={loading} error={error} onReset={onReset}/>
      }
    </>
  );
}
